// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Typography,
} from '@mui/material';
import { Controller, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { memo, useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseAutocomplete, BaseInput, Loading } from '~/components/common';
import { FieldPOProduct } from '~/components/module/job';
// Others
import { IFormPO, IPayloadEditPO, IPO, IVendorPO } from '~/utils/interface/po';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { createPO, editPO, getPONumber, getVendorList } from '~/thunks/po/poThunk';
import { dataTimeZone, removeEmptyObjects } from '~/utils/helper/common';
// Styles, images, icons
import styles from './FormPOJob.module.scss';

type Props = {
  po?: IPO;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormPOJob = (props: Props) => {
  //#region Destructuring Props
  const { po, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/po']);
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormPO>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vendorOption, setVendorOption] = useState<IBaseOption[]>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetPONumber = useCallback(
    (timeZone: string) => {
      dispatch(getPONumber(timeZone))
        .unwrap()
        .then((res) => {
          if (!res.data) return;
          setValue('poNumber', res.data?.poNumber);
        })
        .catch((err) => {});
    },
    [dispatch, setValue]
  );

  const handleGetVendor = useCallback(
    (params: IQueryParamBase) => {
      dispatch(getVendorList(params))
        .unwrap()
        .then((resp) => {
          const { responses } = resp.data;
          if (!responses) {
            return;
          }

          const options: IBaseOption[] = responses.map((res) => {
            return {
              label: res.name,
              value: res.id,
            };
          });

          setVendorOption(options);
        })
        .catch((err) => {});
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (po) {
      const formatData: IFormPO = {
        poNumber: po.poNumber || EMPTY_STRING,
        vendor: po.vendor || EMPTY_STRING,
        receiptNumber: po.receiptNumber || EMPTY_STRING,
        products: po.products ?? [],
        total: po.total ?? DEFAULT_NUMBER_ZERO,
      };
      reset(formatData);
    }
  }, [po, reset]);

  useEffect(() => {
    if (!po?.poNumber) {
      const timeZone = dataTimeZone();
      handleGetPONumber(timeZone);
    }
  }, [po?.poNumber, handleGetPONumber]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
    };

    handleGetVendor(params);
  }, [handleGetVendor]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (values: IFormPO) => {
    if (po?.id) {
      handleEditPO(values);
    } else {
      handleCreatePO(values);
    }
  };

  const handleCreatePO = (values: IFormPO) => {
    if (!jobId) return;
    const { vendor, ...restValues } = values;
    const matchedVendor = vendorOption?.find((t) => t.value === values.vendor?.id);
    const vendorFormat: IVendorPO = matchedVendor
      ? {
          id: values.vendor?.id,
          name: values.vendor?.name || EMPTY_STRING,
        }
      : {
          name: values.vendor?.name || EMPTY_STRING,
        };

    const filteredProducts = (restValues.products ?? []).filter(
      (p) => typeof p.name === 'string' && p.name.trim().length > 0
    );

    const newData = removeEmptyObjects({
      ...restValues,
      products: filteredProducts,
      vendor: vendorFormat,
    }) as IFormPO;

    setIsLoading(true);
    dispatch(
      createPO({
        ...newData,
        jobId: jobId,
      })
    )
      .unwrap()
      .then((res) => {
        handleCloseForm();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditPO = (values: IFormPO) => {
    if (!po?.id) return;
    setIsLoading(true);
    const { vendor, ...restValues } = values;

    let vendorFormat: IVendorPO | null = null;

    if (vendor) {
      const matchedVendor = vendorOption?.find((t) => t.value === vendor.id);
      vendorFormat = matchedVendor
        ? {
            id: vendor.id,
            name: vendor.name || EMPTY_STRING,
          }
        : {
            name: vendor.name || EMPTY_STRING,
          };
    }

    const payload: IPayloadEditPO = {
      poId: po?.id,
      body: {
        ...restValues,
        vendor: vendorFormat,
      },
    };
    dispatch(editPO(payload))
      .unwrap()
      .then((res) => {
        handleCloseForm();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleCloseForm = () => {
    onClose();
    reset(defaultValuesForm);
  };

  //#endregion Handle Function

  return (
    <Dialog id='formPOComponent' open={true} fullWidth maxWidth={'md'} onClose={handleCloseForm}>
      <DialogTitle>{po?.id ? t('form_po_edit_title') : t('form_po_add_title')}</DialogTitle>
      <FormProvider
        {...({
          control,
          watch,
          setValue,
          formState: { errors, isDirty },
        } as UseFormReturn<IFormPO>)}
      >
        <Box component={'form'} className={cx('form')} onSubmit={handleSubmit(handleSubmitForm)}>
          <DialogContent dividers={true} className={cx('contentForm')}>
            <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'24px'}>
              <Grid2 size={4}>
                <Controller
                  name='vendor'
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseAutocomplete
                        value={value?.id}
                        multiple={false}
                        label={t('form_po_vendor_label')}
                        options={vendorOption ?? []}
                        onChange={(newValue) => {
                          if (newValue && typeof newValue === 'object' && !Array.isArray(newValue)) {
                            const formatData: IVendorPO = {
                              id: newValue.value,
                              name: newValue?.label,
                            };

                            onChange(formatData);
                            return;
                          }

                          onChange(newValue);
                        }}
                        errorMessage={errors.vendor?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Grid2 size={4}>
                <Controller
                  name='poNumber'
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInput
                        label={t('form_po_number_po_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors.poNumber?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Grid2 size={4}>
                <Controller
                  name='receiptNumber'
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <BaseInput
                        label={t('form_po_receipt_label')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors.receiptNumber?.message}
                      />
                    );
                  }}
                />
              </Grid2>

              <Box component={'div'} className={cx('productWrap')}>
                <Typography component={'h3'} className={cx('groupTitle')}>
                  {t('form_po_products_title')}
                </Typography>

                <FieldPOProduct />
              </Box>
            </Grid2>
          </DialogContent>

          <DialogActions className={cx('actionsForm')}>
            <Button variant='outlined' onClick={handleCloseForm}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={po?.id ? !isDirty : false}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </DialogActions>

          {isLoading && <Loading />}
        </Box>
      </FormProvider>
    </Dialog>
  );
};

export default memo(FormPOJob);
